import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import chevronDownIcon from "url:shared-assets/icons/chevronDown.svg";

interface DropdownProps {
  label?: string;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  menuClassName?: string;
  borderColor?: string;
  children?: React.ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
  showIcon?: boolean;
  hasBorder?: boolean;
  preferDirection?: "up" | "down"; // New prop
}

const DropDown: React.FC<DropdownProps> = ({
  label = "Select an option",
  disabled = false,
  className = "",
  buttonClassName = "",
  menuClassName = "",
  borderColor = "border-indigo-700",
  children,
  onOpen,
  onClose,
  isOpen: controlledIsOpen,
  setIsOpen: controlledSetIsOpen,
  showIcon = true,
  hasBorder = true,
  preferDirection = "up", // Default to 'up'
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);

  // Determine if the component is controlled
  const isControlled = controlledSetIsOpen != null;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
  const setIsOpen = isControlled ? controlledSetIsOpen : setInternalIsOpen;

  const MARGIN = 8;

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  // Handle clicks outside to close the dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, setIsOpen]);

  // Handle onOpen and onClose callbacks
  useEffect(() => {
    if (isOpen) {
      onOpen && onOpen();
    } else {
      onClose && onClose();
    }
  }, [isOpen, onOpen, onClose]);

  // Determine dropdown direction based on preferDirection and available space
  useLayoutEffect(() => {
    if (isOpen && containerRef.current && menuRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();

      const menuHeight = menuRect.height;
      const spaceBelow = window.innerHeight - containerRect.bottom;
      const spaceAbove = containerRect.top;

      if (preferDirection === "up") {
        if (spaceAbove >= menuHeight + MARGIN) {
          setDropUp(true); // Open upwards if enough space above
        } else if (spaceBelow >= menuHeight + MARGIN) {
          setDropUp(false); // Open downwards if enough space below
        } else {
          setDropUp(true); // Default to upwards if no space
        }
      } else {
        // preferDirection === 'down'
        if (spaceBelow >= menuHeight + MARGIN) {
          setDropUp(false); // Open downwards if enough space below
        } else if (spaceAbove >= menuHeight + MARGIN) {
          setDropUp(true); // Open upwards if enough space above
        } else {
          setDropUp(false); // Default to downwards if no space
        }
      }
    }
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${className}`}
      style={{ overflow: "visible" }}
    >
      <button
        type="button"
        onClick={toggleDropdown}
        disabled={disabled}
        className={`
          ${buttonClassName} border ${borderColor} 
          w-full flex items-center justify-between
          bg-indigo-1000 rounded-md px-3 py-3 text-gray-400`}
      >
        <span>{label}</span>
        {showIcon && (
          <img
            src={chevronDownIcon}
            alt="Toggle dropdown"
            className={`h-4 w-4 ml-2 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          className={`
            ${menuClassName}
            absolute w-full
            ${dropUp ? "bottom-full mb-1" : "top-full mt-1"}
            ${hasBorder && "border border-indigo-700"}
            rounded shadow-md z-50
          `}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default DropDown;
